import {
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Card, Divider, Form, Input, message, Space, Steps, Typography } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { Step } = Steps;

const Register: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, isLoading } = useAuthStore();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [userData, setUserData] = useState<any>({});

  const handleUserRegistration = async (values: any) => {
    setUserData({ ...userData, ...values });
    setCurrentStep(1);
  };

  const handleOrganizationRegistration = async (values: any) => {
    try {
      // First register the user
      await register(userData);

      // Then create organization if provided
      if (values.organizationName) {
        await api.post('/organizations', {
          name: values.organizationName,
          slug: values.organizationSlug,
          description: values.organizationDescription,
        });
      }

      message.success(t('register.success'));
      navigate('/');
    } catch (error) {
      // Error is handled in the store
    }
  };

  const steps = [
    {
      title: t('register.stepPersonal'),
      content: (
        <Form
          form={form}
          name="user_registration"
          onFinish={handleUserRegistration}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="firstName"
            rules={[{ required: true, message: t('register.firstNameRequired') }]}
          >
            <Input prefix={<UserOutlined />} placeholder={t('register.firstName')} />
          </Form.Item>

          <Form.Item
            name="lastName"
            rules={[{ required: true, message: t('register.lastNameRequired') }]}
          >
            <Input prefix={<UserOutlined />} placeholder={t('register.lastName')} />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('register.emailRequired') },
              { type: 'email', message: t('register.emailInvalid') },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder={t('register.email')} />
          </Form.Item>

          <Form.Item name="phone">
            <Input prefix={<PhoneOutlined />} placeholder={t('register.phone')} />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: t('register.passwordRequired') },
              { min: 6, message: t('register.passwordMin') },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder={t('register.password')} />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: t('register.confirmRequired') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('register.passwordMismatch')));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder={t('register.confirmPassword')} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {t('register.next')}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      title: t('register.stepOrganization'),
      content: (
        <Form
          form={form}
          name="organization_registration"
          onFinish={handleOrganizationRegistration}
          layout="vertical"
          size="large"
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
            {t('register.organizationHint')}
          </Text>

          <Form.Item name="organizationName">
            <Input prefix={<TeamOutlined />} placeholder={t('register.organizationName')} />
          </Form.Item>

          <Form.Item
            name="organizationSlug"
            rules={[
              {
                pattern: /^[a-z0-9-]*$/,
                message: t('register.slugPattern'),
              },
            ]}
          >
            <Input placeholder={t('register.slug')} />
          </Form.Item>

          <Form.Item name="organizationDescription">
            <Input.TextArea placeholder={t('register.organizationDescription')} rows={4} />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%' }}>
              <Button onClick={() => setCurrentStep(0)}>{t('register.back')}</Button>
              <Button type="primary" htmlType="submit" loading={isLoading} style={{ flex: 1 }}>
                {t('register.finish')}
              </Button>
            </Space>
          </Form.Item>

          <Form.Item>
            <Button type="link" onClick={() => handleOrganizationRegistration({})} block>
              {t('register.skip')}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Card style={{ width: 500, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={2}>{t('register.title')}</Title>
            <Text type="secondary">{t('register.subtitle')}</Text>
          </div>

          <Steps current={currentStep} size="small">
            {steps.map((item) => (
              <Step key={item.title} title={item.title} />
            ))}
          </Steps>

          <div>{steps[currentStep].content}</div>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Text>
              {t('register.haveAccount')} <Link to="/login">{t('register.loginLink')}</Link>
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default Register;
